import { ConfirmModal } from "@components/ConfirmModal";
import { DeleteIcon } from "@components/Icons/Delete";
import { QuestionMarkIcon } from "@components/Icons/QuestionMark";
import { Input } from "@components/Input";
import { Textarea } from "@components/Input/Textarea";
import { Modal } from "@components/Modal";
import { OKModal } from "@components/OKModal";
import { reorder } from "@helpers/reorder";
import { uid } from "@helpers/uid";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import { collection, deleteDoc, deleteField, doc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { FC, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { useForm } from "react-hook-form";
import { FirebaseContext } from "src/helpers/firebase";
import { useEvent, useEventId } from "src/helpers/useEvent";
import { useToast } from "src/helpers/useToast";
import { Question } from "./Question";
import { BettingScenario } from "./BettingScenario";
import { ChallengeLink } from "./ChallengeLink";
import { CloseBettingModal } from "./CloseBettingModal";
import { QuestionPhotoModal } from "./QuestionPhotoModal";
import { IntroPhotoModal } from "./IntroPhotoModal";
import { AudioModal } from "./AudioModal";

type Props = {
  challenge: (Challenge & Solution) | undefined;
  close: () => void;
};

export const EditModal: FC<Props> = ({ challenge, close }) => {
  const { t, i18n } = useTranslation("admin");
  const locale = (i18n.language || "en") as AppLocale;
  const toast = useToast();
  const { getKeyName, getTranslatedValue } = useTranslatedKey();
  const defaultChallengeType = challenge?.type || "photo";
  const {
    register,
    setValue,
    handleSubmit,
    getValues,
    watch,
    formState: { errors },
  } = useForm<any>({
    resolver: (data) => {
      if (data.type === "quiz") {
        const questions = data.questions;
        const hasEmptyQuestion = questions.some((question) => getTranslatedValue(question, "question") === "");
        if (hasEmptyQuestion) {
          toast(t("fieldRequired"), "🤷‍♂️", "error");
          return {
            values: data,
            errors: {
              questions: {
                message: t("fieldRequired"),
              },
            },
          };
        }
      }
      return {
        values: data,
        errors: {},
      };
    },
    defaultValues: useMemo(() => {
      const challengeQuestions = (challenge as any)?.questions || [];
      const questions = challengeQuestions.map((question) => {
        const processedQuestion: any = {
          ...question,
          id: question.id || uid(), // Ensure each question has an ID
          question: getTranslatedValue(question, "question") || "",
          answer0: getTranslatedValue(question, "answer0") || "",
          answer1: getTranslatedValue(question, "answer1") || "",
          answer2: getTranslatedValue(question, "answer2") || "",
          answer3: getTranslatedValue(question, "answer3") || "",
        };

        // Only include questionHasPhoto if it's true (avoid undefined in form state)
        if (question.questionHasPhoto) {
          processedQuestion.questionHasPhoto = true;
        }

        return processedQuestion;
      });
      const challengeScenarios = (challenge as any)?.scenarios || [];
      const scenarios = challengeScenarios.map((scenario) => ({
        ...scenario,
        scenario: getTranslatedValue(scenario, "scenario") || "",
        option0: getTranslatedValue(scenario, "option0") || "",
        option1: getTranslatedValue(scenario, "option1") || "",
        option2: getTranslatedValue(scenario, "option2") || "",
        option3: getTranslatedValue(scenario, "option3") || "",
      }));
      const title = getTranslatedValue(challenge, "title") || "";
      const about = getTranslatedValue(challenge, "about") || "";
      const copy = getTranslatedValue(challenge, "copy") || "";
      const type = defaultChallengeType || "";
      const showCorrectAnswers = (challenge as QuizChallenge)?.showCorrectAnswers || false;
      const introPhotoUri = (challenge as { introPhotoUri?: string })?.introPhotoUri || "";
      const audioUri = (challenge as InfoChallenge)?.audioUri || "";
      const reward = challenge?.reward || 0;
      const autoTranslate = false;

      return {
        title,
        about,
        copy,
        type,
        reward,
        questions,
        scenarios,
        showCorrectAnswers,
        introPhotoUri,
        audioUri,
        autoTranslate,
      };
    }, [challenge]),
  });

  const [activeTab, setActiveTab] = useState<number | undefined>(undefined);
  const [showAutoTranslateConfirm, setShowAutoTranslateConfirm] = useState(false);
  const [activeQuestionPhotoIndex, setActiveQuestionPhotoIndex] = useState<number | undefined>(undefined);
  const [showIntroPhotoModal, setShowIntroPhotoModal] = useState(false);
  const [showAudioModal, setShowAudioModal] = useState(false);

  const { functions, firestore } = useContext(FirebaseContext);
  const eventId = useEventId();
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const openConfirmDelete = () => setShowConfirmDelete(true);
  const closeConfirmDelete = () => setShowConfirmDelete(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [activeScenarioToResolve, setActiveScenarioToResolve] = useState<number | undefined>(undefined);

  const openResolveModal = (scenarioIndex: number) => {
    setActiveScenarioToResolve(scenarioIndex);
    setShowResolveModal(true);
  };

  const closeResolveModal = () => {
    setShowResolveModal(false);
    setActiveScenarioToResolve(undefined);
  };
  const [autoTranslateChallenge] = useHttpsCallable<any, any>(functions, "autoTranslateChallengeV2");
  const [concludeBetting] = useHttpsCallable<API.ConcludeBettingRequest, API.ConcludeBettingResponse>(
    functions,
    "concludeBettingV2",
  );

  const [event] = useEvent();
  //https://reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);

  const onDelete = async () => {
    setDeleting(true);
    const docRef = doc(firestore, `events/${eventId}/challenges/${challenge.id}`);
    await deleteDoc(docRef);

    // In some edge-cases challenge order normalisation doesn't exist, if so we can ignore the below operation
    if (event.order) {
      const newOrder = event.order.filter((id) => id !== challenge.id);
      const eventRef = doc(firestore, `events/${eventId}`);
      await updateDoc(eventRef, { order: newOrder });

      // Below is a special case to clear event from being considered as 'prepopulated' by AI auto generation
      // This basically means that the user has chosen to remove all of the (AI prepopulated) challenges,
      // thus it doesn't make sense to consider this event prepopulated anymore
      const removingLastChallenge = newOrder.length === 1;
      if (removingLastChallenge) await updateDoc(eventRef, { isPrepopulated: false });
    }

    setDeleting(false);
    toast(t("deleted"), "👍");
    close();
  };

  // Helper function to save challenge data without UI feedback
  const saveChallenge = async (formData: any, showToast = true, closeModal = true) => {
    const {
      reward,
      title,
      about,
      copy,
      questions,
      scenarios,
      showCorrectAnswers,
      introPhotoUri,
      audioUri,
      autoTranslate,
    } = formData;

    const rewardValue =
      challengeType === "song" || challengeType === "betting" || challengeType === "info" ? 0 : parseInt(reward);
    const shouldAutoTranslate = autoTranslate !== false; // Default to true

    const payload: Record<string, unknown> = { reward: rewardValue, title, about, copy, showCorrectAnswers };
    payload[getKeyName("title")] = title;
    payload[getKeyName("about")] = about;
    payload[getKeyName("copy")] = copy;
    if (supportsIntroPhoto) {
      payload.introPhotoUri = introPhotoUri ? introPhotoUri : deleteField();
    }
    if (challengeType === "info") {
      payload.audioUri = audioUri ? audioUri : deleteField();
    }

    const processedQuestions = (questions || []).map((question) => {
      const processedQuestion: any = {
        ...question,
        id: question.id || uid(), // Ensure each question has an ID
        [getKeyName("question")]: question.question,
        [getKeyName("answer0")]: question.answer0,
        [getKeyName("answer1")]: question.answer1,
        [getKeyName("answer2")]: question.answer2,
        [getKeyName("answer3")]: question.answer3,
      };

      // Only include questionHasPhoto if it's true (Firestore doesn't accept undefined)
      if (question.questionHasPhoto) {
        processedQuestion.questionHasPhoto = true;
      } else {
        // Explicitly delete questionHasPhoto if it's false/undefined (from spread operator)
        delete processedQuestion.questionHasPhoto;
      }

      return processedQuestion;
    });
    payload["questions"] = processedQuestions;

    const processedScenarios = (scenarios || []).map((scenario) => {
      const processedScenario = {
        ...scenario,
        [getKeyName("scenario")]: scenario.scenario,
        [getKeyName("option0")]: scenario.option0,
        [getKeyName("option1")]: scenario.option1,
        [getKeyName("option2")]: scenario.option2,
        [getKeyName("option3")]: scenario.option3,
      };

      // Only include correctOutcome if it's defined (not undefined)
      if (scenario.correctOutcome !== undefined) {
        processedScenario.correctOutcome = scenario.correctOutcome;
      }

      return processedScenario;
    });
    payload["scenarios"] = processedScenarios;

    const docRef = doc(firestore, `events/${eventId}/challenges/${challenge.id}`);

    // Ensure the challenge ID is included in the payload
    const payloadWithId = {
      ...payload,
      id: challenge.id, // Ensure ID is preserved
    };

    await setDoc(docRef, payloadWithId, { merge: true });

    // Auto-translate asynchronously in background if enabled (not available in trial mode or single language events)
    if (shouldAutoTranslate && shouldShowAutoTranslate) {
      try {
        const challengeData = {
          type: challengeType,
          title,
          about,
          copy,
          questions: challengeType === "quiz" ? processedQuestions : undefined,
          scenarios: challengeType === "betting" ? processedScenarios : undefined,
        };

        const sourceLanguage = locale;

        // Fire and forget - don't await, let it run in background
        autoTranslateChallenge({
          eventId,
          challengeId: challenge.id,
          challengeData,
          sourceLanguage,
        })
          .then((result) => {
            const responseData = result?.data as { success: boolean };
            if (responseData?.success) toast(t("translationComplete"), "🌍", "success");
            else toast(t("translationError"), "⚠️", "warning");
          })
          .catch((error) => {
            console.error("Translation failed:", error);
            toast(t("translationError"), "⚠️", "warning");
          });

        // Show immediate feedback that translation started
        if (showToast) toast(t("translating"), "🔄", "info");
      } catch (error) {
        console.error("Translation setup error:", error);
        if (showToast) toast(t("translationError"), "⚠️", "warning");
      }
    }

    if (showToast && !shouldAutoTranslate) toast(t("updated"), "👍"); // we get a separate confirmation toast when auto-translate completes
    if (closeModal) close();
  };

  const onSubmit = async (formData: any) => {
    setUpdating(true);
    try {
      await saveChallenge(formData);
    } finally {
      setUpdating(false);
    }
  };
  const questions: QuizQuestion[] = getValues("questions") || [];
  const scenarios: BettingScenario[] = getValues("scenarios") || [];

  const onNewQuestionClick = () => {
    if (isTrial) return;
    const newQuestions = [
      ...questions,
      { id: uid(), question: "", answer0: "", answer1: "", answer2: "", answer3: "", correctAnswer: 0 },
    ];
    setValue("questions", newQuestions);
    forceUpdate();
  };

  const handleQuestionPhotoChange = (questionIndex: number, questionHasPhoto: boolean) => {
    const updatedQuestions = [...questions];
    const updatedQuestion = { ...updatedQuestions[questionIndex] };

    if (questionHasPhoto) {
      updatedQuestion.questionHasPhoto = true;
    } else {
      // Remove questionHasPhoto property entirely when photo is deleted (Firestore doesn't accept undefined)
      delete updatedQuestion.questionHasPhoto;
    }

    updatedQuestions[questionIndex] = updatedQuestion;
    setValue("questions", updatedQuestions);
    setActiveQuestionPhotoIndex(undefined);
    forceUpdate();
  };

  const onNewScenarioClick = () => {
    if (isTrial) return;
    const newScenarios = [
      ...scenarios,
      { id: uid(), scenario: "", option0: "", option1: "", option2: "", option3: "" },
    ];
    setValue("scenarios", newScenarios);
    forceUpdate();
  };

  const challengeType = watch("type");

  const isQuiz = useMemo(() => challengeType === "quiz", [challengeType]);
  useEffect(() => {
    if (isQuiz) setActiveTab(1);
  }, [isQuiz]);

  const isPhoto = useMemo(() => ["photo", "greeting", "video", "photovideo"].includes(challengeType), [challengeType]);
  useEffect(() => {
    if (isPhoto) setActiveTab(1);
  }, [isPhoto]);

  const isGreeting = useMemo(() => challengeType === "greeting", [challengeType]);
  const isSong = useMemo(() => challengeType === "song", [challengeType]);
  const isBetting = useMemo(() => challengeType === "betting", [challengeType]);
  const isVoting = useMemo(() => challengeType === "voting", [challengeType]);
  const isInfo = useMemo(() => challengeType === "info", [challengeType]);

  // Quiz challenges always support an intro photo; photo/video challenges only for hardcoded events (e.g. eventId === "foobar")
  const challengeIntroPhotosEnabled = ["sloveandthunder", "beatricesmergvakaris"].includes(eventId);
  const supportsIntroPhoto =
    challengeType === "quiz" ||
    (challengeIntroPhotosEnabled && ["photo", "video", "photovideo"].includes(challengeType));

  useEffect(() => {
    if (isSong) setActiveTab(1);
  }, [isSong]);

  useEffect(() => {
    if (isBetting) setActiveTab(1);
  }, [isBetting]);

  useEffect(() => {
    if (isVoting) setActiveTab(1);
  }, [isVoting]);

  const isOracle = useMemo(() => challengeType === "oracle", [challengeType]);
  // Without this, activeTab stays undefined and every `!activeTab || ...` content div renders at once
  useEffect(() => {
    if (isInfo || isOracle) setActiveTab(1);
  }, [isInfo, isOracle]);

  const [activeQuestionToRemove, setActiveQuestionToRemove] = useState<number | undefined>(undefined);
  const [activeScenarioToRemove, setActiveScenarioToRemove] = useState<number | undefined>(undefined);
  const [activeSongToRemove, setActiveSongToRemove] = useState<string | undefined>(undefined);
  const [activeVotingEntryToRemove, setActiveVotingEntryToRemove] = useState<string | undefined>(undefined);
  const [showCloseBettingModal, setShowCloseBettingModal] = useState(false);
  const [closing, setClosing] = useState(false);

  const onRemove = (index: number) => {
    const newQuestions = questions.filter((_, i) => i !== index);
    setValue("questions", newQuestions);
    forceUpdate();
  };

  const onRemoveScenario = (index: number) => {
    const newScenarios = scenarios.filter((_, i) => i !== index);
    setValue("scenarios", newScenarios);
    forceUpdate();
  };

  const onRemoveSong = async (songId: string) => {
    try {
      const songRequests = event?.songRequests ?? [];
      const updatedSongRequests = songRequests.filter((song) => song.id !== songId);
      const eventDocRef = doc(firestore, `events/${eventId}`);
      await updateDoc(eventDocRef, { songRequests: updatedSongRequests });
      toast(t("songRemoved"), "✅", "success");
    } catch (error) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error("Error removing song:", error);
    }
  };

  const onRemoveVotingEntry = async (entryId: string) => {
    try {
      const votingChallenge = challenge as VotingChallenge;
      const currentEntries = votingChallenge?.entries ?? [];
      const updatedEntries = currentEntries.filter((entry) => entry.id !== entryId);
      const challengeDocRef = doc(firestore, `events/${eventId}/challenges/${challenge.id}`);
      await updateDoc(challengeDocRef, { entries: updatedEntries });
      toast(t("votingEntryRemoved"), "✅", "success");
    } catch (error) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error("Error removing voting entry:", error);
    }
  };

  // Fetch vote counts for voting challenge entries (only when viewing a voting challenge)
  const [voteCountsMap, setVoteCountsMap] = useState<Record<string, number>>({});
  const votingChallengeId = challenge?.type === "voting" ? challenge.id : null;

  useEffect(() => {
    // Skip fetch entirely for non-voting challenges
    if (!votingChallengeId) return;

    const fetchVoteCounts = async () => {
      try {
        // Get all teams
        const teamsSnapshot = await getDocs(collection(firestore, `events/${eventId}/teams`));
        const counts: Record<string, number> = {};

        // For each team, get their solution for this challenge
        await Promise.all(
          teamsSnapshot.docs.map(async (teamDoc) => {
            const solutionsSnapshot = await getDocs(
              collection(firestore, `events/${eventId}/teams/${teamDoc.id}/solutions`),
            );
            const solution = solutionsSnapshot.docs.find((d) => d.id === votingChallengeId);

            if (solution?.exists()) {
              const data = solution.data() as VotingSolution;
              if (data.votedForEntryId) {
                counts[data.votedForEntryId] = (counts[data.votedForEntryId] ?? 0) + 1;
              }
            }
          }),
        );

        setVoteCountsMap(counts);
      } catch (error) {
        console.error("Error fetching vote counts:", error);
      }
    };

    fetchVoteCounts();
  }, [votingChallengeId, eventId, firestore]);

  // Get voting entries from the challenge document, sorted by vote count then alphabetically
  const votingEntriesForChallenge = useMemo(() => {
    if (challenge?.type !== "voting") return [];
    const votingChallenge = challenge as VotingChallenge;
    const entries = votingChallenge?.entries ?? [];

    return entries
      .map((entry) => ({
        ...entry,
        voteCount: voteCountsMap[entry.id] ?? 0,
      }))
      .sort((a, b) => b.voteCount - a.voteCount || a.entryName.localeCompare(b.entryName));
  }, [challenge, voteCountsMap]);

  const tabWidth =
    challengeType === "quiz" ||
    challengeType === "song" ||
    challengeType === "betting" ||
    challengeType === "voting" ||
    challengeType === "info"
      ? "w-1/3"
      : "w-1/2";

  const moveItem = (index: number, direction: "up" | "down") => {
    if (direction === "up" && index > 0) {
      const newOrder = reorder(questions, index, index - 1);
      setValue("questions", newOrder);
    } else if (direction === "down" && index < questions.length - 1) {
      const newOrder = reorder(questions, index, index + 1);
      setValue("questions", newOrder);
    }
    forceUpdate();
  };

  const moveScenario = (index: number, direction: "up" | "down") => {
    if (direction === "up" && index > 0) {
      const newOrder = reorder(scenarios, index, index - 1);
      setValue("scenarios", newOrder);
    } else if (direction === "down" && index < scenarios.length - 1) {
      const newOrder = reorder(scenarios, index, index + 1);
      setValue("scenarios", newOrder);
    }
    forceUpdate();
  };

  const toggleHideChallenge = async (challengeId: string, value: boolean) => {
    const docRef = doc(firestore, `events/${eventId}/challenges/${challengeId}`);
    await updateDoc(docRef, { hidden: value });
  };

  const onResolveOutcome = (scenarioIndex: number, outcome: number) => {
    const updatedScenarios = [...scenarios];
    updatedScenarios[scenarioIndex] = {
      ...updatedScenarios[scenarioIndex],
      correctOutcome: outcome,
    };
    setValue("scenarios", updatedScenarios);
    forceUpdate();
    toast(t("outcomeResolved"), "✅", "success");
  };

  const onCloseBetting = async () => {
    setClosing(true);
    try {
      // Validate required data before proceeding
      if (!challenge?.id || typeof challenge.id !== "string" || challenge.id.trim() === "") {
        throw new Error("Challenge ID is missing or invalid");
      }

      if (!eventId || typeof eventId !== "string" || eventId.trim() === "") {
        throw new Error("Event ID is missing or invalid");
      }

      // First, save the challenge to ensure all resolved outcomes are persisted to Firestore
      const formData = getValues();
      await saveChallenge(
        {
          ...formData,
          autoTranslate: false, // Don't auto-translate when concluding betting
        },
        false,
        false,
      ); // Don't show toast or close modal yet

      // Add a small delay to ensure Firestore write is complete
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Retry mechanism for concludeBetting call
      let result;
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          result = await concludeBetting({
            eventId,
            challengeId: challenge.id,
          });
          break; // Success, exit retry loop
        } catch (error) {
          retryCount++;
          if (retryCount >= maxRetries) {
            throw error; // Re-throw if max retries reached
          }

          // Wait before retrying (exponential backoff)
          const delay = Math.pow(2, retryCount) * 1000; // 2s, 4s, 8s
          console.log(`Retrying concludeBetting (attempt ${retryCount + 1}/${maxRetries}) after ${delay}ms delay`);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }

      if (result?.data?.success) {
        const { results } = result.data;
        toast(
          `${t("bettingClosed")} - ${results.totalParticipants} ${t("participants")}, ${results.totalWinners} ${t(
            "winners",
          )}`,
          "🔒",
          "success",
        );
        setShowCloseBettingModal(false);
        close();
      } else {
        throw new Error("Failed to conclude betting");
      }
    } catch (error) {
      console.error("Error closing betting:", error);

      // Provide more specific error messages based on error type
      let errorMessage = t("problemOccured");
      if (error instanceof Error) {
        if (error.message.includes("Challenge ID")) {
          errorMessage = t("challengeIdMissing") || "Challenge ID is missing";
        } else if (error.message.includes("Event ID")) {
          errorMessage = t("eventIdMissing") || "Event ID is missing";
        } else if (error.message.includes("not-found")) {
          errorMessage = t("challengeNotFound") || "Challenge not found";
        } else if (error.message.includes("permission-denied")) {
          errorMessage = t("permissionDenied") || "Permission denied";
        } else if (error.message.includes("failed-precondition")) {
          errorMessage = t("bettingAlreadyClosed") || "Betting is already closed";
        }
      }

      toast(errorMessage, "🤷‍♂️", "error");
    } finally {
      setClosing(false);
    }
  };

  // Check if all scenarios have resolved outcomes
  const allScenariosResolved = scenarios.every((scenario) => scenario.correctOutcome !== undefined);
  const bettingChallenge = challenge as BettingChallenge;
  const isBettingClosed = bettingChallenge?.bettingsClosed;

  const isTrial = event?.isTrial || false;

  // Check if event has multiple languages enabled
  const enabledLocalesCount = event?.locales ? Object.values(event.locales).filter(Boolean).length : 1;
  const hasMultipleLanguages = enabledLocalesCount > 1;
  const shouldShowAutoTranslate = hasMultipleLanguages;

  return (
    <>
      <ConfirmModal
        messageCopy={t("areYouSureDeleteChallenge")}
        show={showConfirmDelete}
        onCancel={closeConfirmDelete}
        onConfirm={onDelete}
      />
      <ConfirmModal
        show={activeQuestionToRemove !== undefined}
        onCancel={() => setActiveQuestionToRemove(undefined)}
        onConfirm={() => {
          if (activeQuestionToRemove !== undefined) onRemove(activeQuestionToRemove);
        }}
      />
      <ConfirmModal
        show={activeScenarioToRemove !== undefined}
        onCancel={() => setActiveScenarioToRemove(undefined)}
        onConfirm={() => {
          if (activeScenarioToRemove !== undefined) onRemoveScenario(activeScenarioToRemove);
        }}
      />
      <ConfirmModal
        messageCopy={t("areYouSureDeleteSong")}
        show={activeSongToRemove !== undefined}
        onCancel={() => setActiveSongToRemove(undefined)}
        onConfirm={() => {
          if (activeSongToRemove !== undefined) {
            onRemoveSong(activeSongToRemove);
            setActiveSongToRemove(undefined);
          }
        }}
      />
      <ConfirmModal
        messageCopy={t("areYouSureDeleteVotingEntry")}
        show={activeVotingEntryToRemove !== undefined}
        onCancel={() => setActiveVotingEntryToRemove(undefined)}
        onConfirm={() => {
          if (activeVotingEntryToRemove !== undefined) {
            onRemoveVotingEntry(activeVotingEntryToRemove);
            setActiveVotingEntryToRemove(undefined);
          }
        }}
      />
      <OKModal
        show={showAutoTranslateConfirm}
        messageCopy={t("autoTranslateConfirm")}
        actionCopy={t("enable")}
        onOK={() => {
          setValue("autoTranslate", true);
          setShowAutoTranslateConfirm(false);
          forceUpdate();
        }}
      />
      {showIntroPhotoModal && challenge?.id && (
        <IntroPhotoModal
          show={true}
          onClose={() => setShowIntroPhotoModal(false)}
          challengeId={challenge.id}
          introPhotoUri={getValues("introPhotoUri") || undefined}
          onPhotoChange={(uri) => {
            setValue("introPhotoUri", uri || "");
            forceUpdate();
          }}
        />
      )}
      {showAudioModal && challenge?.id && (
        <AudioModal
          show={true}
          onClose={() => setShowAudioModal(false)}
          challengeId={challenge.id}
          audioUri={getValues("audioUri") || undefined}
          onAudioChange={(uri) => {
            setValue("audioUri", uri || "");
            forceUpdate();
          }}
        />
      )}
      {activeQuestionPhotoIndex !== undefined && questions[activeQuestionPhotoIndex]?.id && challenge?.id && (
        <QuestionPhotoModal
          show={true}
          onClose={() => setActiveQuestionPhotoIndex(undefined)}
          questionId={questions[activeQuestionPhotoIndex].id!}
          challengeId={challenge.id}
          questionHasPhoto={!!questions[activeQuestionPhotoIndex].questionHasPhoto}
          onPhotoChange={(questionHasPhoto) => handleQuestionPhotoChange(activeQuestionPhotoIndex, questionHasPhoto)}
        />
      )}
      <CloseBettingModal
        show={showCloseBettingModal}
        onCancel={() => setShowCloseBettingModal(false)}
        loading={closing}
        onConfirm={onCloseBetting}
        scenarios={scenarios}
        challenge={challenge as BettingChallenge}
      />
      <Modal show={showResolveModal} onCancel={closeResolveModal} className="z-50" size="lg">
        <h3 className="text-lg font-bold">{t("markCorrectOutcome")}</h3>
        <p className="pt-4 pb-6">{t("selectCorrectOutcome")}</p>

        <div className="space-y-2">
          {activeScenarioToResolve !== undefined &&
            scenarios[activeScenarioToResolve] &&
            [0, 1, 2, 3].map((optionIndex) => {
              const scenario = scenarios[activeScenarioToResolve];
              const optionKey = `option${optionIndex}` as keyof BettingScenario;
              const optionText = scenario?.[optionKey] as string;
              if (!optionText || optionText.trim() === "") return null;

              return (
                <button
                  key={optionIndex}
                  type="button"
                  onClick={() => {
                    onResolveOutcome?.(activeScenarioToResolve, optionIndex);
                    closeResolveModal();
                  }}
                  className="btn-outline btn-primary btn w-full justify-start"
                >
                  {t("option")} {optionIndex + 1}: {optionText}
                </button>
              );
            })}
        </div>

        <div className="mt-6 flex justify-end">
          <button type="button" onClick={closeResolveModal} className="btn">
            {t("cancel")}
          </button>
        </div>
      </Modal>
      <Modal show={!!challenge} onCancel={close}>
        <h3 className="mt-6 text-lg font-bold">
          {t("edit")}: {getTranslatedValue(challenge, "title")}
        </h3>
        <div className="mt-2 text-sm">{t(challengeType)}</div>
        <form onSubmit={handleSubmit(onSubmit)} className="overflow-y pt-4">
          <div className="tabs mb-4 mt-8">
            <a
              className={`tab-bordered tab ${activeTab && activeTab === 1 ? "tab-active" : ""} ${tabWidth}`}
              onClick={() => setActiveTab(1)}
            >
              {t("details")}
            </a>
            <a
              className={`tab-bordered tab ${tabWidth} ${challengeType === "quiz" ? "" : "hidden"} ${
                activeTab && activeTab === 2 ? "tab-active" : ""
              } w-1/3`}
              onClick={() => setActiveTab(2)}
            >
              {t("questions")}
            </a>
            <a
              className={`tab-bordered tab ${tabWidth} ${challengeType === "betting" ? "" : "hidden"} ${
                activeTab && activeTab === 2 ? "tab-active" : ""
              } w-1/3`}
              onClick={() => setActiveTab(2)}
            >
              {t("scenarios")}
            </a>
            <a
              className={`tab-bordered tab ${tabWidth} ${challengeType === "song" ? "" : "hidden"} ${
                activeTab && activeTab === 3 ? "tab-active" : ""
              } w-1/3`}
              onClick={() => setActiveTab(3)}
            >
              {t("songRequests")}
            </a>
            <a
              className={`tab-bordered tab ${tabWidth} ${challengeType === "voting" ? "" : "hidden"} ${
                activeTab && activeTab === 5 ? "tab-active" : ""
              } w-1/3`}
              onClick={() => setActiveTab(5)}
            >
              {t("votingEntries")}
            </a>
            <a
              className={`tab-bordered tab ${tabWidth} ${challengeType === "info" ? "" : "hidden"} ${
                activeTab && activeTab === 6 ? "tab-active" : ""
              } w-1/3`}
              onClick={() => setActiveTab(6)}
            >
              {t("challengeLinkTab")}
            </a>
            <a
              className={`tab-bordered tab ${activeTab && activeTab === 4 ? "tab-active" : ""} ${tabWidth}`}
              onClick={() => setActiveTab(4)}
            >
              {t("deleteAndHide")}
            </a>
          </div>
          <div className={`${!activeTab || activeTab === 1 ? "" : "hidden"}`}>
            <Input
              label={t("challenge")}
              name="title"
              required
              requiredMessage={t("fieldRequired")}
              errors={errors}
              register={register}
              className="max-w-lg"
            />
            <span className="mt-2" />
            <Textarea
              label={t("about")}
              name="about"
              rows={2}
              errors={errors}
              register={register}
              className="max-w-lg"
            />
            {!isGreeting && !isSong && !isBetting && !isVoting && (
              <Textarea
                label={t("copy")}
                name="copy"
                required
                rows={4}
                requiredMessage={t("fieldRequired")}
                errors={errors}
                register={register}
                className="max-w-lg"
              />
            )}
            {!isSong && !isBetting && !isInfo && (
              <Input
                label={t("reward")}
                name="reward"
                type="number"
                required
                requiredMessage={t("fieldRequired")}
                errors={errors}
                register={register}
                className="max-w-lg"
              />
            )}
            {challengeType === "quiz" && (
              <div className="form-control mt-4 w-full">
                <label className="label cursor-pointer">
                  <span className="label-text mr-3 text-sm">{t("showCorrectAnswers")}</span>
                  <input
                    type="checkbox"
                    className="toggle-primary toggle"
                    checked={getValues(`showCorrectAnswers`)}
                    onClick={(e) => {
                      const value = (e.target as any).checked;
                      setValue(`showCorrectAnswers`, value);
                      forceUpdate();
                    }}
                  />
                </label>
              </div>
            )}
            {supportsIntroPhoto && (
              <div className="form-control mt-4 w-full">
                <button
                  type="button"
                  onClick={() => setShowIntroPhotoModal(true)}
                  className={`btn-outline btn-sm btn ${getValues("introPhotoUri") ? "btn-primary" : "btn-secondary"}`}
                >
                  {getValues("introPhotoUri") ? t("introPhotoChange") : t("introPhotoAdd")}
                </button>
              </div>
            )}
            {isInfo && (
              <div className="form-control mt-4 w-full">
                <button
                  type="button"
                  onClick={() => setShowAudioModal(true)}
                  className={`btn-outline btn-sm btn ${getValues("audioUri") ? "btn-primary" : "btn-secondary"}`}
                >
                  {getValues("audioUri") ? t("audioChange") : t("audioAdd")}
                </button>
              </div>
            )}
            {shouldShowAutoTranslate && (
              <div className="form-control mt-4 w-full">
                <div
                  className={`${isTrial ? "tooltip-premium tooltip text-neutral" : ""}`}
                  data-tip={isTrial ? t("premiumFeature") : ""}
                >
                  <label className="label cursor-pointer">
                    <span className="label-text mr-3 text-sm">{t("autoTranslate")}</span>
                    <input
                      type="checkbox"
                      className={`toggle-primary toggle ${isTrial ? "toggle-disabled" : ""}`}
                      defaultChecked={false}
                      disabled={isTrial}
                      onClick={(e) => {
                        if (!isTrial) {
                          const value = (e.target as any).checked;
                          if (value) {
                            setShowAutoTranslateConfirm(true);
                          } else {
                            setValue("autoTranslate", false);
                          }
                          forceUpdate();
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
          <div className={`${activeTab === 2 && challengeType === "quiz" ? "" : "hidden"}`}>
            <div>
              {questions.map(({ question, id, questionHasPhoto }, i) => {
                const key = id ?? question ?? i;
                return (
                  <div key={key}>
                    <Question
                      moveItem={moveItem}
                      isFirst={i === 0}
                      isLast={i === questions.length - 1}
                      questionIndex={i}
                      correctAnswer={getValues(`questions.${i}.correctAnswer`)}
                      updateCorrectAnswer={(correctAnswer) => {
                        setValue(`questions.${i}.correctAnswer`, correctAnswer);
                        forceUpdate();
                      }}
                      register={register}
                      errors={errors}
                      setActiveQuestionToRemove={(index) => setActiveQuestionToRemove(index)}
                      onPhotoClick={() => setActiveQuestionPhotoIndex(i)}
                      questionHasPhoto={!!questionHasPhoto}
                    />
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center">
              <div className="tooltip-premium tooltip text-neutral" data-tip={t("premiumFeature")}>
                <button
                  onClick={() => onNewQuestionClick()}
                  className={`btn-outline btn-primary btn my-6 h-10 w-28 text-lg ${isTrial ? "btn-disabled" : ""}`}
                  disabled={isTrial}
                  type="button"
                >
                  +
                </button>
              </div>
            </div>
          </div>
          <div className={`${activeTab === 2 && challengeType === "betting" ? "" : "hidden"}`}>
            {isBettingClosed ? (
              // Show concluded betting summary
              <div className="space-y-6">
                <div className="rounded-lg border border-info bg-info bg-opacity-10 p-4">
                  <h4 className="mb-2 text-lg font-semibold text-info">{t("bettingConcluded")}</h4>
                  <p className="text-sm text-base-content/70">{t("bettingConcludedMessage")}</p>
                </div>

                <div>
                  <h4 className="text-md mb-4 font-semibold">{t("finalOutcomes")}</h4>
                  <div className="space-y-3">
                    {scenarios.map((scenario, index) => {
                      const scenarioText = getTranslatedValue(scenario, "scenario");
                      const correctOutcome = scenario.correctOutcome;
                      const optionKey = `option${correctOutcome}` as keyof BettingScenario;
                      const correctOptionText =
                        correctOutcome !== undefined ? getTranslatedValue(scenario, optionKey) : "Not resolved";

                      return (
                        <div
                          key={scenario.id || index}
                          className="rounded-lg border border-success bg-success bg-opacity-10 p-4"
                        >
                          <p className="mb-2 font-medium">{scenarioText}</p>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-success">✓ {t("correctOutcome")}:</span>
                            <span className="text-sm">
                              {correctOutcome !== undefined
                                ? `${t("option")} ${correctOutcome + 1} - ${correctOptionText}`
                                : "Not resolved"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              // Show editable scenarios
              <>
                <div>
                  {scenarios.map(({ scenario, id }, i) => {
                    const key = id ?? scenario ?? i;
                    return (
                      <div key={key}>
                        <BettingScenario
                          moveScenario={moveScenario}
                          isFirst={i === 0}
                          isLast={i === scenarios.length - 1}
                          scenarioIndex={i}
                          register={register}
                          errors={errors}
                          setActiveScenarioToRemove={(index) => setActiveScenarioToRemove(index)}
                          scenario={scenarios[i]}
                          onResolveOutcome={onResolveOutcome}
                          showResolveButton={!isBettingClosed}
                          onOpenResolveModal={openResolveModal}
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-center">
                  <div className="tooltip-premium tooltip text-neutral" data-tip={t("premiumFeature")}>
                    <button
                      onClick={() => onNewScenarioClick()}
                      className={`btn-outline btn-primary btn my-6 h-10 w-28 text-lg ${isTrial ? "btn-disabled" : ""}`}
                      disabled={isTrial}
                      type="button"
                    >
                      +
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className={`${activeTab === 3 ? "" : "hidden"}`}>
            <div className="mt-8">
              <h4 className="mb-4 text-lg font-semibold">{t("manageSongRequests")}</h4>
              <p className="mb-6 text-sm text-base-content/70">{t("manageSongRequestsDescription")}</p>

              {!event?.songRequests || event.songRequests.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-base-content/60">{t("noSongRequestsYet")}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {event.songRequests.map((song) => (
                    <div
                      key={song.id}
                      className="flex items-center justify-between rounded-lg border border-base-300 bg-base-200 bg-opacity-50 p-4"
                    >
                      <div className="flex-1">
                        <p className="font-semibold">{song.songName}</p>
                        <p className="text-sm text-base-content/70">
                          {t("requestedBy")}: {song.teamName}
                        </p>
                        <p className="text-xs text-base-content/60">
                          {t("votes")}: {song.votedTeams.length}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveSongToRemove(song.id)}
                        className="btn-outline btn-error btn-sm btn"
                      >
                        <DeleteIcon className="h-4 w-4" />
                        {t("remove")}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className={`${activeTab === 5 ? "" : "hidden"}`}>
            <div className="mt-8">
              <h4 className="mb-4 text-lg font-semibold">{t("manageVotingEntries")}</h4>
              <p className="mb-6 text-sm text-base-content/70">{t("manageVotingEntriesDescription")}</p>

              {votingEntriesForChallenge.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <p className="text-base-content/60">{t("noVotingEntriesYet")}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {votingEntriesForChallenge.map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between rounded-lg border border-base-300 bg-base-200 bg-opacity-50 p-4"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-content">
                            {entry.voteCount}
                          </div>
                          <div>
                            <p className="font-semibold">{entry.entryName}</p>
                            <p className="text-sm text-base-content/70">
                              {t("addedBy")}: {entry.teamName}
                            </p>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveVotingEntryToRemove(entry.id)}
                        className="btn-outline btn-error btn-sm btn"
                      >
                        <DeleteIcon className="h-4 w-4" />
                        {t("remove")}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {isInfo && (
            <div className={`${activeTab === 6 ? "" : "hidden"}`}>
              <ChallengeLink challenge={challenge} />
            </div>
          )}
          <div className={`${!activeTab || activeTab === 4 ? "" : "hidden"}`}>
            <div className="mt-12 mb-16 flex flex-col items-center justify-center">
              <p className="mb-4 text-center">{t("areYouSureHideChallenge")}</p>
              <div className="flex flex-row items-center">
                <label className="label cursor-pointer" htmlFor={"hide-toggle"}>
                  <div className="label-text mr-3 text-sm">
                    <span className="label-text">{t("hide")}</span>
                  </div>
                  <input
                    id={"hide-toggle"}
                    type="checkbox"
                    className={`toggle-primary toggle`}
                    defaultChecked={challenge.hidden ?? false}
                    onClick={(e) => {
                      const value = (e.target as HTMLInputElement).checked;
                      toggleHideChallenge(challenge.id, value);
                    }}
                  />
                </label>
                <div className="tooltip-tight-right tooltip tooltip-top flex items-end" data-tip={t("hideTooltip")}>
                  <QuestionMarkIcon className="h-5 h-5 text-neutral opacity-60" strokeWidth={0.2} />
                </div>
              </div>
              <div className="my-2 my-8 h-0.5 w-full bg-gray-200" />
              <p className="mb-12 text-center">{t("permanentlyDeleteChallenge")}</p>
              <button
                type="button"
                disabled={deleting}
                onClick={openConfirmDelete}
                className={`inline-flex w-full max-w-3xs items-center justify-center rounded-md border border-red-600 bg-white px-5 py-2 text-sm font-semibold text-red-600 shadow-sm hover:bg-red-600 hover:text-white ${
                  deleting ? "loading" : ""
                }`}
              >
                <DeleteIcon className="mr-2 h-4 h-4" />
                {t("delete")}
              </button>
            </div>
          </div>
          <div>
            {/* Show Close Betting button for betting challenges when all scenarios are resolved and betting is not closed */}
            {isBetting && allScenariosResolved && !isBettingClosed && (
              <div className="mt-6 mb-4">
                <button
                  type="button"
                  onClick={() => setShowCloseBettingModal(true)}
                  className="hover:bg-success-focus inline-flex w-full justify-center rounded-md border border-green-700 bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  {t("saveAndCloseBetting")}
                </button>
              </div>
            )}

            <div className="mt-3 mb-2 flex flex-col">
              <button
                type="submit"
                disabled={updating}
                className={`inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-base-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2 ${
                  updating ? "loading" : ""
                }`}
              >
                {t("save")}
              </button>
              <button
                type="button"
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto"
                onClick={close}
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </>
  );
};
